import { useMemo } from 'react';
import { Check, Pencil, X, Undo2, RotateCcw, ArrowDownLeft, ArrowUpRight, CreditCard, Repeat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import Money from '../../components/kit/Money';
import Pill from '../../components/kit/Pill';
import { useData } from '../../store/data';
import { toDate } from '../transactions/entries';
import type { PlannedEntry } from './plannedEntries';

/** Loại khoản = biểu tượng tròn có màu (thu xanh, chi đỏ, trả thẻ & nợ xanh dương) — không cần nhãn chữ trên từng dòng. */
export const KIND = {
  income: { Icon: ArrowDownLeft, cls: 'bg-good-soft text-good', label: 'Thu' },
  expense: { Icon: ArrowUpRight, cls: 'bg-bad-soft text-bad', label: 'Chi' },
  payment: { Icon: CreditCard, cls: 'bg-accent text-accent-foreground', label: 'Trả thẻ & nợ' }
} as const;
const WEEKDAY = ['Chủ nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];
const WEEKDAY_SHORT = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

export interface Handlers { onConfirm: (e: PlannedEntry) => void; onEdit: (e: PlannedEntry) => void; onSkip: (e: PlannedEntry) => void; onReopen?: (e: PlannedEntry) => void; onUndo?: (e: PlannedEntry) => void }

const fmtDate = (d: Date | null) => (d ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` : '');

/** Dòng "đã ghi" của khoản đã xác nhận: ngày xác nhận + số/ngày thực tế nếu khác dự kiến (chi/thu tra từ sổ). */
function ConfirmedInfo({ e }: { e: PlannedEntry }) {
  const actual = useData(s => {
    if (e.status !== 'confirmed') return undefined;
    if (e.kind === 'expense') return s.data.expenses.find((x: any) => x.id === e.doc.actualExpenseId);
    if (e.kind === 'income') return s.data.incomes.find((x: any) => x.id === e.doc.actualIncomeId);
    return undefined;
  });
  if (e.status !== 'confirmed') return null;
  const when = toDate(e.doc.confirmedAt);
  const real = actual ? Number(actual.amount) : null;
  const realDate = actual ? toDate(actual.transactionDate) : null;
  const differs = real !== null && real !== e.amount;
  return <p className="mt-1 text-sm text-muted-foreground">Đã ghi{realDate ? ` ngày ${fmtDate(realDate)}` : when ? ` ngày ${fmtDate(when)}` : ''}{differs ? ` · thực tế ${real!.toLocaleString('vi-VN')} đ (dự kiến ${e.amount.toLocaleString('vi-VN')} đ)` : ''}</p>;
}

/** Dòng chính là TÊN khoản (ghi chú/tên mẫu); danh mục và ví xuống dòng phụ. Khoản trả thẻ/nợ vốn đã có tên đích nên giữ nguyên. */
export const entryName = (e: PlannedEntry): string => (e.kind !== 'payment' && e.detail && e.detail !== e.title ? e.detail : e.title);

export function Row({ e, h }: { e: PlannedEntry; h: Handlers }) {
  const k = KIND[e.kind];
  const name = entryName(e);
  const dim = e.virtual || e.status !== 'pending';
  const base = e.kind === 'payment' ? (e.walletName ? `Từ ${e.walletName}` : 'Chưa chọn ví') : [name !== e.title ? e.title : '', e.walletName || 'Chưa chọn ví'].filter(Boolean).join(' · ');
  const sub = e.followed ? `${base} · theo số tháng trước` : base;
  return (
    <div data-planned-row className={cn('flex flex-wrap items-center gap-x-3 gap-y-2 py-3 pr-3 sm:pr-4', dim && 'opacity-70')}>
      <span role="img" aria-label={k.label} className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', k.cls)}><k.Icon size={17} aria-hidden /></span>
      <div className="min-w-0 flex-1 basis-40">
        <p className="flex items-center gap-1.5 text-base font-medium">
          <span className="truncate">{name}</span>
          {e.recurring && <Repeat size={13} className="shrink-0 text-muted-foreground" role="img" aria-label="Định kỳ" />}
          {e.daysLate > 0 ? <Pill tone={e.daysLate > 30 ? 'bad' : 'warn'}>Quá hạn {e.daysLate} ngày</Pill> : e.estimate ? <Pill>Ước tính</Pill> : null}
        </p>
        <p className="truncate text-sm text-muted-foreground">{sub}</p>
        <ConfirmedInfo e={e} />
        {e.status === 'cancelled' && e.cancelReason && <p className="mt-1 text-sm text-muted-foreground">Lý do bỏ qua: {e.cancelReason}</p>}
        {e.daysLate > 60 && <p className="mt-1 text-sm text-bad">Quá 60 ngày chưa xác nhận. Nếu không còn phát sinh, hãy bỏ qua để đóng sổ.</p>}
      </div>
      <Money value={e.kind === 'income' ? e.amount : -e.amount} plus={e.kind === 'income'} tone={e.kind === 'income' ? 'in' : undefined}
        className={e.status === 'cancelled' ? 'line-through opacity-60' : undefined} />
      {e.status === 'confirmed' && !e.virtual && h.onUndo && (
        <Button size="sm" variant="ghost" onClick={() => h.onUndo!(e)} aria-label={`Hoàn tác ${name}`}><Undo2 size={14} aria-hidden /> Hoàn tác</Button>
      )}
      {e.status === 'cancelled' && !e.virtual && h.onReopen && (
        <Button size="sm" variant="ghost" onClick={() => h.onReopen!(e)} aria-label={`Mở lại ${name}`}><RotateCcw size={14} aria-hidden /> Mở lại</Button>
      )}
      {e.status === 'pending' && (
        <div className="flex items-center gap-1">
          <Button size="sm" variant={e.virtual ? 'outline' : 'default'} onClick={() => h.onConfirm(e)} aria-label={`Xác nhận ${name}`}><Check size={14} aria-hidden /> Xác nhận</Button>
          <Button size="icon" variant="ghost" onClick={() => h.onEdit(e)} aria-label={`Sửa ${name}`} title="Sửa"><Pencil size={15} /></Button>
          <Button size="icon" variant="ghost" onClick={() => h.onSkip(e)} aria-label={`Bỏ qua ${name}`} title="Bỏ qua"><X size={16} /></Button>
        </div>
      )}
    </div>
  );
}

const dayKey = (d: Date | null) => (d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : 'none');

/** Danh sách theo NGÀY: số ngày slab ở cột trái (như tờ lịch), các khoản của ngày đó bên phải, ngăn cách bằng đường xé. */
export function DayList({ items, numeral, h }: { items: PlannedEntry[]; numeral: string; h: Handlers }) {
  const buckets = useMemo(() => {
    const m = new Map<string, PlannedEntry[]>();
    for (const e of items) m.set(dayKey(e.date), [...(m.get(dayKey(e.date)) ?? []), e]);
    return [...m.values()];
  }, [items]);
  // Kỳ gồm nhiều tháng (quý/năm): ghi thêm tháng/năm dưới số ngày để khỏi nhầm
  const multiMonth = new Set(items.map(e => (e.date ? `${e.date.getFullYear()}-${e.date.getMonth()}` : ''))).size > 1;
  return (
    <Card className="overflow-hidden p-0">
      {buckets.map((b, bi) => {
        const d = b[0].date;
        return (
          <div key={dayKey(d) + bi} className={cn('grid grid-cols-[3.5rem_1fr] sm:grid-cols-[5rem_1fr]', bi > 0 && 'perf-top')}>
            <div className="pt-3.5 text-center" aria-hidden={!d}>
              {d && (<>
                <p className={cn('font-slab num text-3xl font-bold leading-none sm:text-4xl', numeral)}>{String(d.getDate()).padStart(2, '0')}</p>
                {multiMonth
                  ? <p className="num mt-1 text-xs text-muted-foreground">{String(d.getMonth() + 1).padStart(2, '0')}/{d.getFullYear()}</p>
                  : <p className="mt-1 text-xs text-muted-foreground"><span className="sm:hidden">{WEEKDAY_SHORT[d.getDay()]}</span><span className="hidden sm:inline">{WEEKDAY[d.getDay()]}</span></p>}
              </>)}
            </div>
            <div>{b.map((e, i) => <div key={e.key} className={cn(i > 0 && 'perf-top')}><Row e={e} h={h} /></div>)}</div>
          </div>
        );
      })}
    </Card>
  );
}

